import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Descriptions, Form, Input, Radio, Rate, Space, Spin, Statistic, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useAuthStore } from '@/store/authStore';
import { fmtDateTime, parseJsonArray, RECOMMENDATIONS, recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;

/** One interviewer's scorecard. Each panel member submits their own; HR closes the round on the interview page. */
const InterviewScorecardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.interview(id || ''),
    queryFn: () => hrmsService.getInterview(id!),
    enabled: Boolean(id),
  });

  const questions: any[] = row?.criteria?.questions || [];
  const mine = useMemo(() => (row?.feedback || []).find((f: any) => f.interviewer_user_id === userId), [row, userId]);

  useEffect(() => {
    if (!row) return;
    const prev = parseJsonArray(mine?.ratings_json);
    form.setFieldsValue({
      recommendation: mine?.recommendation,
      comments: mine?.comments,
      ratings: Object.fromEntries(
        questions.map((q) => {
          const p: any = prev.find((r: any) => r.code === q.code);
          return [q.code, { rating: p?.rating, comment: p?.comment }];
        })
      ),
    });
  }, [row, mine, questions, form]);

  const ratings = Form.useWatch('ratings', form) as Record<string, { rating?: number }> | undefined;
  const preview = useMemo(() => {
    let w = 0;
    let sum = 0;
    for (const q of questions) {
      const r = ratings?.[q.code]?.rating;
      if (r == null) continue;
      const max = q.max_rating || 5;
      const weight = q.weight || 1;
      sum += (r / max) * weight;
      w += weight;
    }
    return w ? Math.round((sum / w) * 5 * 100) / 100 : null;
  }, [ratings, questions]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await hrmsService.submitInterviewFeedback(id!, {
        recommendation: v.recommendation,
        comments: v.comments,
        ratings: questions
          .filter((q) => v.ratings?.[q.code]?.rating != null)
          .map((q) => ({ code: q.code, rating: v.ratings[q.code].rating, comment: v.ratings[q.code].comment })),
      });
      message.success('Feedback submitted');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.interview(id!) });
      await queryClient.invalidateQueries({ queryKey: ['hrms', 'my-interviews'] });
      navigate(`/hr/interviews/${id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Submit failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Scorecard" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Scorecard" />}>
        <Card>Not found, or you are not on this interview's panel.</Card>
      </WorkspaceLayout>
    );
  }

  const locked = ['Cancelled', 'NoShow'].includes(row.status);
  const sections = [...new Set(questions.map((q) => q.section || 'Criteria'))];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Interview scorecard"
          documentNumber={`${row.application_number} · Round ${row.round_number}`}
          subtitle={
            <Text type="secondary">
              {row.candidate_name} — {row.job_title} · {fmtDateTime(row.scheduled_at)}
            </Text>
          }
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/hr/interviews/${id}`)}>
                Back
              </Button>
              <Button type="primary" icon={<SendOutlined />} loading={saving} disabled={locked} onClick={submit}>
                {mine?.submitted_at ? 'Update feedback' : 'Submit feedback'}
              </Button>
            </Space>
          }
        />
      }
    >
      <div style={{ maxWidth: 860 }}>
        {locked && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={`This interview is ${row.status}; feedback is closed.`} />}
        {mine?.submitted_at && (
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message={`You submitted feedback on ${fmtDateTime(mine.submitted_at)}. You can update it until the round is closed.`} />
        )}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space size={48}>
            <Statistic title="Your weighted score" value={preview ?? '—'} suffix={preview != null ? '/ 5' : undefined} precision={preview != null ? 2 : undefined} />
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Template">{row.criteria?.template_name || 'Default'}</Descriptions.Item>
              <Descriptions.Item label="Criteria">{questions.length}</Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>
        <Form form={form} layout="vertical" disabled={locked}>
          {sections.map((s) => (
            <Card key={s} size="small" title={s} style={{ marginBottom: 16 }}>
              {questions
                .filter((q) => (q.section || 'Criteria') === s)
                .map((q) => (
                  <div key={q.code} style={{ marginBottom: 12 }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                      <span>
                        {q.text} {q.weight && q.weight !== 1 ? <Tag>×{q.weight}</Tag> : null}
                      </span>
                      <Form.Item name={['ratings', q.code, 'rating']} noStyle>
                        <Rate count={q.max_rating || 5} />
                      </Form.Item>
                    </Space>
                    <Form.Item name={['ratings', q.code, 'comment']} style={{ marginBottom: 0, marginTop: 4 }}>
                      <Input placeholder="Evidence / comment (optional)" />
                    </Form.Item>
                  </div>
                ))}
            </Card>
          ))}
          <Card size="small" title="Your recommendation">
            <Form.Item name="recommendation" rules={[{ required: true, message: 'Choose a recommendation' }]}>
              <Radio.Group optionType="button" buttonStyle="solid" options={RECOMMENDATIONS.map((r) => ({ value: r.value, label: r.label }))} />
            </Form.Item>
            <Form.Item name="comments" label="Overall comments" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={4} placeholder="Strengths, concerns, follow-ups for the next round" />
            </Form.Item>
          </Card>
        </Form>
      </div>
    </WorkspaceLayout>
  );
};

export default InterviewScorecardPage;
